import * as React from 'react';
import { memo, useState } from 'react';
import styled from 'styled-components';
import { Box, Card, CardContent } from '@mui/material';
import {
  Col, Flex, Image, Row,
} from 'antd';
import { Draw, Engineering } from '@mui/icons-material';
import { ProjectCardItemType } from '../../../../common/types';
import { getImageAward } from '../../functions';
import { fallbackImgSrc } from '../../consts';
import { colors } from '../../assets/constStyles';
import { useResizeWidthWindow } from '../hook/useResizeWidthWindow';

const DivStyled = styled.div`
  //min-width: 350px;
  width: 100%;
  display: flex;
  flex-direction: column;

  .ant-image{
    width: 100%;
    height: 100%;
  }
  
  .ant-image-img {
    width: 100%;
    height: 100%;
  }
`;

const CardStyled = styled(Card)`
  width: 100%;
  box-shadow: rgba(145, 158, 171, 0.3) 0 0 2px 0, rgba(145, 158, 171, 0.12) 0 12px 24px -4px;
  transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  position: relative;
`;

const TextStyledSecond = styled.p`
  font-size: 1em;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 0.7em;
  }
`;

const TextStyled = styled.p`
  font-size: 1.5em;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.1em;
  }
`;

const TextCatStyled = styled.p`
  font-size: 1.5em;
  font-weight: 600;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 1.1em;
  }
`;

export const SpanCaptionStyled = styled.span`
  font-size: 1em;
  color: rgba(0, 0, 0, 0.45);
  transition: color 0.2s;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${colors.colorGrey};
`;

const StyledImage = styled(Image)`
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  object-position: center;
`;

interface IProjectCardItem {
  dataCard: ProjectCardItemType,
}

const ProjectCardItem: React.FC<IProjectCardItem> = ({ dataCard }) => {
  const [hasError] = useState(false);
  const projectImagesFolder = `${process.env.REACT_APP_S3_STORAGE_URL}/${process.env.REACT_APP_BUCKET}/${dataCard.id}`;

  const size = useResizeWidthWindow();

  return (
        <DivStyled>
            <CardStyled variant="outlined">
                <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundImage: 'url("../img/4028659.jpg")',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.1,
                      zIndex: 0,
                    }}
                />
                <CardContent style={{ flexGrow: 1 }}>
                    <Row>
                        <Col className="gutter-row" xs={24} sm={24} md={24} lg={18} xl={18}>
                            <Flex gap={20}>
                                <Flex align={'baseline'} gap={5}>
                                    <SpanCaptionStyled>Year: </SpanCaptionStyled>
                                    <TextStyled>{dataCard.year}</TextStyled>
                                </Flex>
                                <Flex align={'baseline'} gap={5}>
                                    <SpanCaptionStyled>Category: </SpanCaptionStyled>
                                    <TextCatStyled>{dataCard.category}</TextCatStyled>
                                </Flex>
                            </Flex>
                            {dataCard.woodworker
                                && <Flex align={'baseline'} gap={5} style={{ margin: '20px 0 10px 0' }}>
                                    {/* <SpanCaptionStyled>Woodworker: </SpanCaptionStyled> */}
                                    <TextStyled style={{ fontWeight: 'bold' }}>{dataCard.woodworker}</TextStyled>
                                </Flex>
                            }
                            <Flex gap={20}>
                                {dataCard.generalContractor
                                    && <Flex align={'center'} justify={'center'} gap={5}>
                                        <Engineering style={{ color: 'rgba(0, 0, 0, 0.45)' }}/>
                                        <TextStyledSecond>{dataCard.generalContractor}</TextStyledSecond>
                                    </Flex>
                                }
                                {dataCard.designProfessional
                                    && <Flex align={'center'} justify={'center'} gap={5}>
                                        <Draw style={{ color: 'rgba(0, 0, 0, 0.45)' }}/>
                                        <TextStyledSecond>{dataCard.designProfessional}</TextStyledSecond>
                                    </Flex>
                                }
                            </Flex>
                        </Col>
                        <Col className="gutter-row" xs={24} sm={24} md={24} lg={6} xl={6}>
                            <Flex justify="flex-end" align="baseline">
                                {
                                    dataCard.awards.map((aw, index) => getImageAward(aw.awardName, aw.type, index, size))
                                }
                            </Flex>
                        </Col>
                    </Row>
                    {dataCard.object
                        && <Row style={{ margin: '20px 0px 10px 0px' }}>
                            <Flex align={'center'} justify={'center'} gap={5}>
                                <SpanCaptionStyled>Object: </SpanCaptionStyled>
                                <TextStyledSecond>{dataCard.object}</TextStyledSecond>
                            </Flex>
                        </Row>
                    }
                    <Row gutter={[10, 10]} style={{ height: '180px', overflowY: 'auto' }}>
                        <Image.PreviewGroup>
                            {
                                Array.from({ length: dataCard.countImages }, (_, i) => i + 1)
                                  .map((_, index) => (
                                        <Col className="gutter-row" xs={12} sm={12} md={6} lg={6} xl={6} key={index}>
                                            <ImageWrapper>
                                                <StyledImage
                                                    src={hasError ? fallbackImgSrc : `${projectImagesFolder}/${index + 1}.jpg`}
                                                    preview={!hasError}
                                                />
                                            </ImageWrapper>
                                        </Col>
                                  ))
                            }
                        </Image.PreviewGroup>
                    </Row>
                </CardContent>
            </CardStyled>
        </DivStyled>
  );
};

export default memo(ProjectCardItem);
